import React, { FC, ReactElement } from 'react';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';

import { InfoTooltip } from 'ui';
import { BaseProps } from 'shared/types/base';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material';

const alignOptions = {
  left: 'left',
  right: 'right',
  center: 'center',
};

type TitleProps = {
  bottomLine?: boolean;
  tooltip?: ReactElement | string;
  tooltipClassName?: string;
  align?: keyof typeof alignOptions;
  inline?: boolean;
} & BaseProps;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    color: theme.palette.primary.main,
    paddingBottom: theme.spacing(1),
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
  },
  alignLeft: {
    justifyContent: 'flex-start',
  },
  alignRight: {
    justifyContent: 'flex-end',
  },
  alignCenter: {
    justifyContent: 'center',
  },
  bottomLine: {
    borderBottom: `1px solid ${grey[200]}`,
  },
  tooltipIcon: {
    marginLeft: theme.spacing(1),
  },
  inline: {
    display: 'inline',
  }
}));

const Title: FC<TitleProps> = ({
  bottomLine,
  tooltip = '',
  children,
  align = alignOptions.left,
  className,
  tooltipClassName,
  inline,
}) => {
  const classes = useStyles();
  const rootClassNames = clsx(classes.root, className, {
    [classes.bottomLine]: bottomLine,
    [classes.alignLeft]: align === alignOptions.left,
    [classes.alignRight]: align === alignOptions.right,
    [classes.alignCenter]: align === alignOptions.center,
    [classes.inline]: inline === true,
  });

  return (
    <Typography variant="body1" className={rootClassNames}>
      {children}
      {tooltip ? (
        <InfoTooltip title={tooltip} iconClassName={clsx(classes.tooltipIcon, tooltipClassName)} />
      ) : null}
    </Typography>
  );
};

export default Title;
